import React from 'react';
import clsx from 'clsx';
import styles from './PrintAppointmentDaycare.module.scss';
import { PrintBookingDetailAppointmentViewModel } from '../utils/PrintBookingDetailViewModel';
import FeedingsAndMedicationsInfo from 'components/CreateBooking/MultiplePets/FeedingsAndMedicationsInfo';
import { useMasterData } from 'hooks';
import { getDuration } from 'components/Checkout2/UI/AppointmentData';

type Props = {
    appointment: PrintBookingDetailAppointmentViewModel;
    dateLabel?: string;
    className?: string;
    showDivider?: boolean;
};

export const RadioLine: React.FC<{
    momentLabel?: string;
    mainText: string;
    variant?: 'medicationStyle' | 'feedingStyle';
}> = ({ momentLabel, mainText, variant }) => {
    const variantClass = variant === 'medicationStyle' ? styles.medicationStyle : '';

    return (
        <div className={`${styles.radioRow} ${variantClass}`}>
            <div
                className={styles.radio}
                aria-hidden
            />
            <div className={styles.radioContent}>
                <div className={styles.radioLabel}>
                    {momentLabel && <span className={styles.momentLabel}>{momentLabel}</span>}
                    {mainText}
                </div>
            </div>
        </div>
    );
};

export const PrintAppointmentDaycare: React.FC<Props> = ({ appointment, dateLabel, className, showDivider = true }) => {
    const time = appointment.timeLabel ?? '';
    const service = appointment.serviceName ?? '';

    const md = useMasterData();

    // Daycare-specific data comes from appointment.care or similar shape in the view model
    // We'll defensively read feedingSchedules and medications from appointment.metadata or care properties.
    const feedingSchedules = appointment.feedingSchedules ?? [];
    const hasFeedings = feedingSchedules.length > 0;
    const medications = appointment.medications ?? [];
    const hasMedications = medications.length > 0;
    const durationOption = getDuration(appointment?.raw?.appointment);
    const serviceName = `${service} (${durationOption})`;

    return (
        <div className={clsx(styles.root, className, { [styles.withDivider]: showDivider })}>
            {dateLabel && <span className={styles.dateLabel}>{dateLabel}</span>}

            <div className={styles.serviceLine}>
                <div className={styles.time}>{time}</div>

                <div className={styles.line}>
                    <span className={styles.service}>{serviceName}</span>
                </div>
            </div>

            <FeedingsAndMedicationsInfo
                hasFeedings={hasFeedings}
                hasMedications={hasMedications}
            />

            <div className={styles.subsections}>
                {!hasFeedings && !hasMedications ? (
                    <div className={styles.subsection}>
                        <div className={styles.noScheduleLabel}>No Feeding, No Medications</div>
                    </div>
                ) : (
                    <>
                        {/* Feedings */}
                        <div className={styles.subsection}>
                            {Array.isArray(feedingSchedules) && feedingSchedules.length > 0 ? (
                                feedingSchedules.map((fs: any, idx: number) => (
                                    <div
                                        key={idx}
                                        className={styles.feedingBlock}
                                    >
                                        <div className={styles.scheduleLabel}>Feeding Schedule {idx + 1}</div>
                                        {/* For each moment (Morning/Afternoon/Evening) render a radio row */}
                                        {(fs.momentTypes ?? []).map((mt: number, mIdx: number) => {
                                            let momentLabel = '';
                                            switch (mt) {
                                                case 1:
                                                    momentLabel = 'Morning:';
                                                    break;
                                                case 2:
                                                    momentLabel = 'Afternoon:';
                                                    break;
                                                default:
                                                    momentLabel = 'Evening:';
                                            }

                                            // resolve provisioner name and food type / quantity from masterdata
                                            const provisioner = md.foodProvisionTypes.find(
                                                (p: any) => p.id === fs.foodProvisionTypeId
                                            )?.name;
                                            const foodType = md.foodTypes.find(
                                                (f: any) => f.id === fs.foodTypeId
                                            )?.name;
                                            const unit = md.foodQuantityUnits.find(
                                                (u: any) => u.id === fs.quantity?.unit
                                            )?.name;
                                            const qty = fs.quantity?.quantity
                                                ? `${fs.quantity.quantity} ${unit ?? ''}`.trim()
                                                : undefined;

                                            const providerText =
                                                provisioner === 'Client' || provisioner === 'client'
                                                    ? 'Client provides food'
                                                    : provisioner === 'House'
                                                    ? 'House provides food'
                                                    : provisioner ?? '';

                                            const mainText = [providerText, foodType, fs.brand, qty]
                                                .filter(Boolean)
                                                .join(', ');

                                            return (
                                                <RadioLine
                                                    key={mIdx}
                                                    momentLabel={momentLabel}
                                                    mainText={mainText}
                                                />
                                            );
                                        })}

                                        {fs.feedingInstructions && (
                                            <div className={styles.instructions}>
                                                <span className={styles.instructionsLabel}>FEEDING INSTRUCTIONS:</span>{' '}
                                                {fs.feedingInstructions}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className={styles.scheduleLabel}>No Feeding</div>
                            )}
                        </div>

                        {/* Medications */}
                        <div className={styles.subsection}>
                            {Array.isArray(medications) && medications.length > 0 ? (
                                medications.map((med: any, idx: number) => {
                                    const methodName = md.medicationMethodTypes.find(
                                        (m: any) => m.id === med.methodType
                                    )?.name;
                                    const dosageQty = med.dosage?.quantity;
                                    const dosageUnit = md.medicationDosageUnits.find(
                                        (u: any) => u.id === med.dosage?.unit
                                    )?.name;
                                    const dosageStr = dosageQty ? `${dosageQty} ${dosageUnit ?? ''}`.trim() : undefined;

                                    return (
                                        <div
                                            key={idx}
                                            className={styles.medBlock}
                                        >
                                            <div className={styles.scheduleMedicationLabel}>
                                                Medication {idx + 1} · {med.name ?? med.medicationName ?? 'Medication'}
                                                {methodName ? ` · ${methodName}` : ''}
                                                {dosageStr ? ` · ${dosageStr}` : ''}
                                            </div>

                                            {med.instructions && (
                                                <div className={styles.instructionsMedication}>
                                                    <span className={styles.instructionsLabel}>
                                                        MEDICATION INSTRUCTIONS:
                                                    </span>{' '}
                                                    {med.instructions}
                                                </div>
                                            )}

                                            {/* dose rows */}
                                            <div className={styles.doseRows}>
                                                {Array.isArray(med.times) && med.times.length > 0 ? (
                                                    med.times.map((t: string, doseIdx: number) => (
                                                        <RadioLine
                                                            variant="medicationStyle"
                                                            key={doseIdx}
                                                            momentLabel={`DOSE ${doseIdx + 1}`}
                                                            mainText={t}
                                                        />
                                                    ))
                                                ) : (
                                                    // if no times, still show one Not provided line for the medication doses
                                                    <RadioLine
                                                        variant="medicationStyle"
                                                        momentLabel={'DOSE 1'}
                                                        mainText={'Not provided'}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className={styles.scheduleLabel}>No Medication</div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PrintAppointmentDaycare;
